import { useEffect, useState } from "react";

import { fetchPatientsCount, getDoctorsPays } from "~/actions/dashboard";
import { getAllShipments } from "~/actions/deliveries";
import { useGetUnpaidEventsQuery } from "~/features/events/events-api";
import { AWAITING_ACTION_TAB, statusParamFor } from "~/common/redesign-v1/patients-tabs";
import { selectUser } from "~/slices/user";
import { useAppDispatch, useAppSelector } from "~/store";

export function usePaymentsCount(): number {
  const dispatch = useAppDispatch();

  const user = useAppSelector(selectUser);
  const doctorPays = useAppSelector((state) => state.doctorPays);
  const numberOfPayments = doctorPays.patients.length;

  useEffect(() => {
    // When you log out, this component is rerendered, and doctorTasks.hasBeenLoaded is false,
    // so the request fires.
    // That's why we check if user is authenticated before trying to load the payments.
    if (user.account_id && !doctorPays.hasBeenLoaded) {
      void dispatch(getDoctorsPays());
    }
  }, [user.account_id, doctorPays.hasBeenLoaded, dispatch]);

  return numberOfPayments;
}

export function useShipmentsCount(): number {
  const dispatch = useAppDispatch();

  const user = useAppSelector(selectUser);
  const shipments = useAppSelector((state) => state.shipments);
  const numberOfShipments = shipments.data.length;

  useEffect(() => {
    // When you log out, this component is rerendered, and shipments.hasBeenLoaded is false,
    // so the request fires and we gen an error notification.
    // That's why we check if user is authenticated before trying to load the shipments.
    if (user.account_id && !shipments.hasBeenLoaded) {
      void dispatch(getAllShipments());
    }
  }, [user.account_id, shipments.hasBeenLoaded, dispatch]);

  return numberOfShipments;
}

export function useUnpaidEventsCount(): number {
  const unpaidEventsQuery = useGetUnpaidEventsQuery();
  const numberOfUnpaidEvents = unpaidEventsQuery.isSuccess
    ? Object.values(unpaidEventsQuery.data).reduce((a, b) => a + b, 0)
    : 0;

  return numberOfUnpaidEvents;
}

/** Read the sidebar badge count from the same status group as the «Ждут действий» tab. */
export function useAwaitingPatientsCount(): number {
  const user = useAppSelector(selectUser);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!user.account_id) return;
    let cancelled = false;
    void fetchPatientsCount(statusParamFor(AWAITING_ACTION_TAB)).then((total) => {
      if (!cancelled && typeof total === "number") setCount(total);
    });
    return () => {
      cancelled = true;
    };
  }, [user.account_id]);

  return count;
}
